import React, { useState, useEffect, useRef, useCallback } from 'react';
import apiService from '../services/api.service';
import { sendLeadToClient, updateLeadBonus } from '../api';
import { Lead } from '../types/lead';
import { translateStatus, getStatusColor } from '../utils/statusTranslator';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import BlockIcon from '@mui/icons-material/Block';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { QualityAssessmentModal } from './QualityAssessmentModal';
import { Chip, Button, Autocomplete, TextField, useMediaQuery, useTheme, Card, CardContent, Typography as MuiTypography, Box, Divider, IconButton as MuiIconButton, Popover, FormGroup, FormControlLabel, Checkbox } from '@mui/material';

const LEADS_TABLE_COLUMNS: Array<{ key: string; label: string }> = [
  { key: 'id', label: 'ID' },
  { key: 'phone', label: 'Телефон' },
  { key: 'status', label: 'Статус' },
  { key: 'date', label: 'Дата' },
  { key: 'manager', label: 'Менеджер' },
  { key: 'project', label: 'Проект' },
  { key: 'sent_at', label: 'Дата отправки' },
  { key: 'client_status', label: 'Статус заказчика' },
  { key: 'crm_comment', label: 'Комментарии из CRM' },
  { key: 'ai_comment', label: 'Комментарий от ИИ' },
  { key: 'call', label: 'Звонок' },
  { key: 'note', label: 'Примечание' },
  { key: 'comment', label: 'Комментарий' },
  { key: 'customer_name', label: 'Клиент' },
  { key: 'client_manager', label: 'Менеджер заказчика' },
  { key: 'disput_comment', label: 'Комментарий заказчика' },
  { key: 'bonus', label: 'Бонус' },
  { key: 'quality', label: 'Качество' },
  { key: 'price', label: 'Цена' },
  { key: 'address', label: 'Адрес' },
];

const COLUMN_VISIBILITY_KEY = 'leadsTable.hiddenColumns';

function loadHiddenColumns(): Set<string> {
  try {
    const raw = localStorage.getItem(COLUMN_VISIBILITY_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

interface LeadsTableProps {
  leads: Lead[];
  onEdit?: (lead: Lead) => void;
  onDelete?: (id: number) => void;
  onUpdate?: (leadId: number, updates: any) => void;
  onSent?: () => void;
  onNotify?: (message: string, severity?: 'success' | 'error' | 'warning' | 'info') => void;
  onConfirm?: (opts: { title: string; description?: string; confirmText?: string; cancelText?: string }) => Promise<boolean>;
  onPublishToMarketplace?: (lead: Lead) => void;
}

export const LeadsTable: React.FC<LeadsTableProps> = ({ leads, onEdit, onDelete, onUpdate, onSent, onNotify, onConfirm, onPublishToMarketplace }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [error, setError] = useState<string | null>(null);
  const [playingAudio, setPlayingAudio] = useState<number | null>(null);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  const [audioProgress, setAudioProgress] = useState<{ [key: number]: number }>({});
  const [audioDuration, setAudioDuration] = useState<{ [key: number]: number }>({});
  const [projects, setProjects] = useState<any[]>([]);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);

  // Состояния для оценки качества
  const [qualityModalOpen, setQualityModalOpen] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState<any>(null);
  const [assessmentsByLeadId, setAssessmentsByLeadId] = useState<Record<number, any>>({});

  // Скрытые столбцы (персистятся в localStorage)
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(() => loadHiddenColumns());
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(COLUMN_VISIBILITY_KEY, JSON.stringify(Array.from(hiddenColumns)));
    } catch {
      // localStorage недоступен — игнорируем
    }
  }, [hiddenColumns]);

  const toggleColumn = (key: string) => {
    setHiddenColumns(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const columnIndexByKey = React.useMemo(() => {
    const map: Record<string, number> = {};
    LEADS_TABLE_COLUMNS.forEach((col, idx) => { map[col.key] = idx + 1; });
    return map;
  }, []);

  const hiddenColumnsCss = React.useMemo(() => {
    if (hiddenColumns.size === 0) return '';
    const selectors: string[] = [];
    hiddenColumns.forEach(key => {
      const nth = columnIndexByKey[key];
      if (nth) {
        selectors.push(`.leads-table thead tr > *:nth-child(${nth})`);
        selectors.push(`.leads-table tbody tr > *:nth-child(${nth})`);
      }
    });
    return selectors.length ? `${selectors.join(', ')} { display: none; }` : '';
  }, [hiddenColumns, columnIndexByKey]);

  // Используем useRef для предотвращения дублирования запросов
  const isFetchingProjects = useRef(false);
  const projectsLoaded = useRef(false);

  // Загружаем проекты только один раз при монтировании компонента
  useEffect(() => {
    if (!projectsLoaded.current) {
      projectsLoaded.current = true;
      isFetchingProjects.current = true;
      
      apiService.get(`/projects`)
        .then(response => {
          setProjects(response.data);
          isFetchingProjects.current = false;
        })
        .catch(err => {
          console.error('Ошибка при загрузке проектов:', err);
          isFetchingProjects.current = false;
        });
    }
  }, []); // Пустой массив зависимостей - выполняется только при монтировании

  const fetchQualityAssessments = useCallback(async () => {
    const concurrency = 5;
    let index = 0;

    const runNext = async () => {
      if (index >= leads.length) return;
      const batch = leads.slice(index, index + concurrency);
      index += concurrency;
      
      await Promise.all(batch.map(async (lead) => {
        try {
          const resp = await apiService.get(`/quality-control/assessments/by-lead/${lead.id}`);
          if (resp && resp.status === 200 && resp.data) {
            setAssessmentsByLeadId(prev => ({ ...prev, [lead.id]: resp.data }));
          }
        } catch (err: any) {
          // 404 means no assessment — ignore silently
        }
      }));
      
      await runNext();
    };

    await runNext();
  }, [leads]);

  // Загружаем оценки качества для лидов
  useEffect(() => {
    if (leads.length > 0) {
      fetchQualityAssessments();
    }
  }, [leads, fetchQualityAssessments]);

  const handleQualityClick = (assessment: any, lead: Lead) => {
    setSelectedAssessment(assessment);
    setQualityModalOpen(true);
  };

  const getScoreColor = (score: number) => {
    if (score >= 60) return 'success';
    if (score >= 30) return 'warning';
    return 'default';
  };

  const updateBonusStatus = async (leadId: number, bonus: boolean) => {
    try {
      await updateLeadBonus(leadId, bonus);
      // Не обновляем весь список, так как локальное состояние уже актуально
    } catch (error) {
      console.error('Ошибка при обновлении бонусного статуса:', error);
      setError('Ошибка при обновлении бонусного статуса');
      onNotify && onNotify('Ошибка при обновлении бонусного статуса', 'error');
    }
  };

  const updateLeadField = async (leadId: number, field: string, value: any) => {
    try {
      const updateData: any = {};
      updateData[field] = value;
      await apiService.put(`/leads/${leadId}`, updateData);
      // Не обновляем весь список, так как локальное состояние уже актуально
    } catch (error) {
      console.error(`Ошибка при обновлении поля ${field}:`, error);
      setError(`Ошибка при обновлении поля ${field}`);
      onNotify && onNotify(`Ошибка при обновлении поля ${field}`, 'error');
    }
  };

  const handleSpeedChange = (newSpeed: number) => {
    setPlaybackSpeed(newSpeed);
    if (audioElement && playingAudio !== null) {
      audioElement.playbackRate = newSpeed;
    }
  };

  const handlePlayAudio = (leadId: number, mp3Url: string) => {
    if (playingAudio === leadId) {
      // Остановить воспроизведение
      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }
      setPlayingAudio(null);
      setAudioElement(null);
      setAudioProgress(prev => ({ ...prev, [leadId]: 0 }));
    } else {
      // Остановить предыдущее воспроизведение
      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }
      
      // Начать новое воспроизведение
      const audio = new Audio(mp3Url);
      audio.playbackRate = playbackSpeed;
      
      audio.addEventListener('loadedmetadata', () => {
        setAudioDuration(prev => ({ ...prev, [leadId]: audio.duration }));
      });
      
      audio.addEventListener('timeupdate', () => {
        setAudioProgress(prev => ({ ...prev, [leadId]: audio.currentTime }));
      });
      
      audio.addEventListener('ended', () => {
        setPlayingAudio(null);
        setAudioElement(null);
        setAudioProgress(prev => ({ ...prev, [leadId]: 0 }));
      });
      
      audio.addEventListener('error', () => {
        console.error('Ошибка воспроизведения аудио');
        setPlayingAudio(null);
        setAudioElement(null);
      });
      
      audio.play();
      setPlayingAudio(leadId);
      setAudioElement(audio);
    }
  };

  const handleDownloadAudio = (mp3Url: string, leadId: number) => {
    const link = document.createElement('a');
    link.href = mp3Url;
    link.download = `call_${leadId}.mp3`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSeek = (leadId: number, newTime: number) => {
    if (audioElement && playingAudio === leadId) {
      audioElement.currentTime = newTime;
      setAudioProgress(prev => ({ ...prev, [leadId]: newTime }));
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSendToClient = async (leadId: number) => {
    try {
      // Здесь нужно будет указать client_id, пока используем 1 как пример
      await sendLeadToClient(leadId, 1);
      onNotify && onNotify('Лид успешно отправлен клиенту', 'success');
      // Обновляем страницу после успешной отправки
      if (onSent) {
        onSent();
      }
    } catch (error) {
      console.error('Ошибка при отправке лида клиенту:', error);
      onNotify && onNotify('Ошибка при отправке лида клиенту', 'error');
    }
  };

  const handleEdit = (lead: Lead) => {
    if (onEdit) {
      onEdit(lead);
    }
  };

  const handleMarkAsInvalid = async (id: number) => {
    let confirmed = true;
    if (onConfirm) {
      confirmed = await onConfirm({
        title: 'Пометить лид как брак?',
        description: 'Действие необратимо. Лид будет скрыт из текущего списка.',
        confirmText: 'Да, пометить',
        cancelText: 'Отмена',
      });
    } else {
      // Fallback (нежелательно, но на случай отсутствия onConfirm)
      confirmed = window.confirm('Вы уверены, что хотите пометить этот лид как брак?');
    }

    if (!confirmed) return;

    try {
      await apiService.put(`/leads/${id}`, { invalidLead: true });
      onNotify && onNotify('Лид успешно помечен как брак', 'success');
      // Удаление обрабатывается в родительском компоненте
      if (onDelete) {
        onDelete(id);
      }
    } catch (error) {
      console.error('Ошибка при пометке лида как брак:', error);
      setError('Ошибка при пометке лида как брак');
      onNotify && onNotify('Ошибка при пометке лида как брак', 'error');
    }
  };

  // Очистка аудио при размонтировании компонента
  useEffect(() => {
    return () => {
      if (audioElement) {
        audioElement.pause();
        audioElement.currentTime = 0;
      }
    };
  }, [audioElement]);

  if (error) {
    return <div style={{ color: 'red' }}>{error}</div>;
  }

  const filteredLeads = leads;

  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
        {filteredLeads.length === 0 ? (
          <MuiTypography sx={{ p: 2, textAlign: 'center' }}>Лиды не найдены</MuiTypography>
        ) : (
          filteredLeads.map(lead => (
            <Card 
              key={lead.id} 
              sx={{ 
                borderLeft: lead.duplicate ? '6px solid #ffc107' : 'none',
                backgroundColor: lead.duplicate ? '#fffef3' : 'white'
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box>
                    <MuiTypography variant="subtitle2" color="text.secondary">ID: {lead.id}</MuiTypography>
                    <MuiTypography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                      {lead.phone}
                      {lead.duplicate && (
                        <Chip 
                          label="DUP" 
                          size="small" 
                          sx={{ ml: 1, height: 20, fontSize: 10, backgroundColor: '#ffc107', color: '#000', fontWeight: 'bold' }} 
                        />
                      )}
                    </MuiTypography>
                  </Box>
                  <Chip 
                    label={translateStatus(lead.status)} 
                    size="small"
                    sx={{ 
                      backgroundColor: getStatusColor(lead.status),
                      color: 'white',
                      fontSize: 11
                    }}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <MuiTypography variant="caption" color="text.secondary" display="block">
                    {new Date(lead.created_at).toLocaleString('ru-RU')}
                  </MuiTypography>
                  <MuiTypography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                    Проект: {lead.project_name || '-'}
                  </MuiTypography>
                  {lead.client_status && (
                    <MuiTypography variant="body2" sx={{ mt: 0.5 }}>
                      <strong>Статус CRM:</strong> {lead.client_status}
                    </MuiTypography>
                  )}
                  {lead.last_comment && (
                    <Box sx={{ mt: 1, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                      <MuiTypography variant="caption" color="text.secondary" display="block">
                        Комментарий из CRM {lead.total_comments && lead.total_comments > 1 ? `(всего: ${lead.total_comments})` : ''}:
                      </MuiTypography>
                      <MuiTypography variant="body2" sx={{ fontSize: '0.85rem', mt: 0.5 }}>
                        {lead.last_comment.length > 100 ? lead.last_comment.substring(0, 100) + '...' : lead.last_comment}
                      </MuiTypography>
                    </Box>
                  )}
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <Box sx={{ mb: 2 }}>
                  <MuiTypography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Примечание:</MuiTypography>
                  <textarea
                    value={lead.note || ''}
                    onChange={(e) => onUpdate && onUpdate(lead.id, { note: e.target.value })}
                    onBlur={() => updateLeadField(lead.id, 'note', lead.note || '')}
                    style={{ 
                      width: '100%', 
                      minHeight: '60px', 
                      border: '1px solid #eee', 
                      borderRadius: '4px',
                      padding: '8px',
                      fontSize: '13px',
                      backgroundColor: '#fdfdfd'
                    }}
                  />
                </Box>

                {lead.mp3_url && (
                  <Box sx={{ 
                    backgroundColor: '#f0f7f0', 
                    p: 1.5, 
                    borderRadius: 1, 
                    mb: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <MuiIconButton 
                        size="small" 
                        onClick={() => handlePlayAudio(lead.id, lead.mp3_url!)}
                        sx={{ color: playingAudio === lead.id ? '#f44336' : '#4caf50' }}
                      >
                        {playingAudio === lead.id ? '⏹️' : '▶️'}
                      </MuiIconButton>
                      
                      {playingAudio === lead.id && audioDuration[lead.id] && (
                        <Box sx={{ flexGrow: 1 }}>
                          <input
                            type="range"
                            min="0"
                            max={audioDuration[lead.id]}
                            value={audioProgress[lead.id] || 0}
                            onChange={(e) => handleSeek(lead.id, parseFloat(e.target.value))}
                            style={{ width: '100%', accentColor: '#4caf50' }}
                          />
                          <MuiTypography sx={{ fontSize: 10, textAlign: 'right' }}>
                            {formatTime(audioProgress[lead.id] || 0)} / {formatTime(audioDuration[lead.id])}
                          </MuiTypography>
                        </Box>
                      )}
                      
                      <select
                        value={playbackSpeed}
                        onChange={(e) => handleSpeedChange(parseFloat(e.target.value))}
                        style={{ fontSize: 11, border: '1px solid #ddd', borderRadius: 4 }}
                      >
                        <option value={1}>1x</option>
                        <option value={1.5}>1.5x</option>
                        <option value={2}>2x</option>
                      </select>
                    </Box>
                  </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      color="secondary"
                      disabled={!lead.phone}
                      startIcon={<StorefrontIcon />}
                      onClick={() => onPublishToMarketplace && onPublishToMarketplace(lead)}
                    >
                      МП
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleEdit(lead)}
                    >
                      Ред.
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="small" 
                      color="warning"
                      disabled={lead.is_sent}
                      startIcon={<SendIcon />}
                      onClick={() => handleSendToClient(lead.id)}
                    >
                      {lead.is_sent ? 'Отправлен' : 'Отправить'}
                    </Button>
                  </Box>
                  <MuiIconButton 
                    size="small" 
                    color="error" 
                    onClick={() => handleMarkAsInvalid(lead.id)}
                  >
                    <BlockIcon />
                  </MuiIconButton>
                </Box>
              </CardContent>
            </Card>
          ))
        )}
      </Box>
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      {hiddenColumnsCss && <style>{hiddenColumnsCss}</style>}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', my: 1 }}>
        <Button
          size="small"
          startIcon={<ViewColumnIcon />}
          onClick={(e) => setColumnsMenuAnchor(e.currentTarget)}
          variant="outlined"
        >
          Столбцы{hiddenColumns.size > 0 ? ` (скрыто: ${hiddenColumns.size})` : ''}
        </Button>
        <Popover
          open={!!columnsMenuAnchor}
          anchorEl={columnsMenuAnchor}
          onClose={() => setColumnsMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ p: 2, maxHeight: 400, overflowY: 'auto' }}>
            <MuiTypography variant="subtitle2" sx={{ mb: 1 }}>Видимые столбцы</MuiTypography>
            <FormGroup>
              {LEADS_TABLE_COLUMNS.map(col => (
                <FormControlLabel
                  key={col.key}
                  control={
                    <Checkbox
                      size="small"
                      checked={!hiddenColumns.has(col.key)}
                      onChange={() => toggleColumn(col.key)}
                    />
                  }
                  label={col.label}
                />
              ))}
            </FormGroup>
            <Button size="small" sx={{ mt: 1 }} onClick={() => setHiddenColumns(new Set())}>
              Сбросить
            </Button>
          </Box>
        </Popover>
      </Box>
      <table className="leads-table" style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16 }}>
        <thead>
          <tr style={{ backgroundColor: '#f5f5f5' }}>
            <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '1%' }}>ID</th>
            <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '1%' }}>Телефон</th>
            <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '1%' }}>Статус</th>
            <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '1%' }}>Дата</th>
            <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '1%' }}>Менеджер</th>
            <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '70%', minWidth: '250px' }}>Проект</th>
            <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '1%' }}>Дата отправки</th>
            <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '1%' }}>Статус заказчика</th>
            <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '15%' }}>Комментарии из CRM</th>
            <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '15%' }}>Комментарий от ИИ</th>
            <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '1%' }}>Звонок</th>
            <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '50%' }}>Примечание</th>
            <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '15%' }}>Комментарий</th>
            <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '8%' }}>Клиент</th>
            <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '12%' }}>Менеджер заказчика</th>
            <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '12%' }}>Комментарий заказчика</th>
            <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '1%' }}>Бонус</th>
            <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '8%' }}>Качество</th>
            <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '15%', minWidth: '120px' }}>Цена</th>
            <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '15%' }}>Адрес</th>
            <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '1%' }}></th>
          </tr>
          
        </thead>
        <tbody>
          {filteredLeads.length === 0 ? (
            <tr>
              <td colSpan={19} style={{ padding: 16, textAlign: 'center' }}>
                Лиды не найдены
              </td>
            </tr>
          ) : (
            filteredLeads.map(lead => (
              <tr 
                key={lead.id}
                style={{ 
                  backgroundColor: lead.duplicate ? '#fff3cd' : 'white',
                  borderLeft: lead.duplicate ? '4px solid #ffc107' : undefined
                }}
              >
                <td style={{ padding: 8, border: '1px solid #ddd' }}>{lead.id}</td>
                <td style={{ padding: 8, border: '1px solid #ddd' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {lead.phone}
                    {lead.duplicate && (
                      <span 
                        style={{ 
                          padding: '2px 4px', 
                          backgroundColor: '#ffc107', 
                          color: '#000', 
                          borderRadius: '3px', 
                          fontSize: '10px',
                          fontWeight: 'bold'
                        }}
                        title="Дубликат"
                      >
                        DUP
                      </span>
                    )}
                  </div>
                </td>
                <td style={{ padding: 8, border: '1px solid #ddd' }}>
                  <span style={{ 
                    padding: '2px 6px', 
                    borderRadius: 4, 
                    backgroundColor: getStatusColor(lead.status),
                    color: 'white',
                    fontSize: 12
                  }}>
                    {translateStatus(lead.status)}
                  </span>
                </td>
                <td style={{ padding: 8, border: '1px solid #ddd' }}>
                  {new Date(lead.created_at).toLocaleString('ru-RU', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </td>
                <td style={{ fontSize: '12px', padding: 8, border: '1px solid #ddd' }}>
                  <input
                    type="text"
                    value={lead.manager_name || ''}
                    onChange={(e) => {
                      const newManagerName = e.target.value;
                      // Обновляем локальное состояние немедленно через родительский компонент
                      if (onUpdate) {
                        onUpdate(lead.id, { manager_name: newManagerName });
                      }
                    }}
                    onBlur={() => updateLeadField(lead.id, 'manager_name', lead.manager_name || '')}
                    style={{ 
                      width: '100%', 
                      border: '1px solid #ccc', 
                      backgroundColor: 'white',
                      fontSize: '12px',
                      padding: '4px',
                      outline: 'none'
                    }}
                    placeholder="Имя менеджера..."
                  />
                </td>
                <td style={{ padding: 8, border: '1px solid #ddd', minWidth: '250px' }}>
                  <Autocomplete
                    options={projects}
                    getOptionLabel={option => option.name}
                    value={projects.find(p => p.id === lead.project_id) || null}
                    onChange={(event, newValue) => {
                      const selectedProjectId = newValue ? newValue.id : undefined;
                      const selectedProject = newValue;
                      if (onUpdate) {
                        onUpdate(lead.id, {
                          project_id: selectedProjectId,
                          project_name: selectedProject ? selectedProject.name : '-'
                        });
                      }
                      updateLeadField(lead.id, 'project_id', selectedProjectId);
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField {...params} label="Проект" size="small" placeholder="Найти проект..." />
                    )}
                  />
                </td>
                <td style={{ padding: 8, border: '1px solid #ddd', whiteSpace: 'nowrap' }}>
                  {lead.sent_at ? (
                    new Date(lead.sent_at).toLocaleString('ru-RU', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
                  ) : (
                    <span style={{ color: '#999', fontSize: 12 }}>—</span>
                  )}
                </td>
                <td style={{ padding: 8, border: '1px solid #ddd' }}>
                  {lead.client_status ? (
                    <span style={{
                      padding: '2px 6px',
                      borderRadius: 4,
                      backgroundColor: '#e3f2fd',
                      color: '#1565c0',
                      fontSize: 12,
                      whiteSpace: 'nowrap'
                    }}>
                      {lead.client_status}
                    </span>
                  ) : (
                    <span style={{ color: '#999', fontSize: 12 }}>—</span>
                  )}
                </td>
                <td style={{ padding: 8, border: '1px solid #ddd', maxWidth: '250px' }}>
                  {lead.last_comment ? (
                    <div style={{ position: 'relative', cursor: 'pointer' }} title={lead.last_comment}>
                      <div style={{
                        fontSize: 12,
                        color: '#333',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {lead.last_comment.length > 50 ? lead.last_comment.substring(0, 50) + '...' : lead.last_comment}
                      </div>
                      {lead.total_comments && lead.total_comments > 1 && (
                        <span style={{
                          fontSize: 10,
                          color: '#666',
                          marginTop: 4,
                          display: 'block'
                        }}>
                          Всего: {lead.total_comments}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span style={{ color: '#999', fontSize: 12 }}>—</span>
                  )}
                </td>
                <td style={{ padding: 8, border: '1px solid #ddd', maxWidth: 260, verticalAlign: 'top' }}>
                  {lead.ai_comment ? (
                    <div style={{ fontSize: 12, color: '#333', whiteSpace: 'pre-wrap' }}>
                      {lead.ai_name ? <div style={{ fontWeight: 600 }}>👤 {lead.ai_name}</div> : null}
                      <div>{lead.ai_comment}</div>
                      <Button
                        size="small"
                        variant="text"
                        onClick={async () => {
                          try {
                            await apiService.post(`/leads/${lead.id}/ai-generate`, {});
                            onNotify && onNotify('AI обновлён', 'success');
                          } catch (e: any) {
                            onNotify && onNotify(e.message || 'Ошибка AI', 'error');
                          }
                        }}
                        sx={{ textTransform: 'none', fontSize: 10, mt: 0.5 }}
                      >
                        ⟳ Перегенерировать
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={async () => {
                        try {
                          const resp = await apiService.post(`/leads/${lead.id}/ai-generate`, {});
                          if (resp.data?.success) {
                            if (onUpdate) {
                              onUpdate(lead.id, { ai_comment: resp.data.ai_comment, ai_name: resp.data.ai_name });
                            }
                            onNotify && onNotify('AI сгенерирован', 'success');
                          } else {
                            onNotify && onNotify(resp.data?.message || 'AI недоступен', 'warning');
                          }
                        } catch (e: any) {
                          onNotify && onNotify(e.message || 'Ошибка AI', 'error');
                        }
                      }}
                      sx={{ textTransform: 'none', fontSize: 11 }}
                    >
                      Сгенерировать
                    </Button>
                  )}
                </td>
                <td style={{ padding: 8, border: '1px solid #ddd' }}>
                  {lead.mp3_url ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
                      <button
                        onClick={() => handlePlayAudio(lead.id, lead.mp3_url!)}
                        style={{ 
                          padding: '4px 8px', 
                          backgroundColor: 'transparent', 
                          color: playingAudio === lead.id ? '#f44336' : '#4caf50', 
                          border: 'none', 
                          borderRadius: 4, 
                          cursor: 'pointer',
                          fontSize: 12
                        }}
                        title={playingAudio === lead.id ? 'Остановить' : 'Воспроизвести'}
                      >
                        {playingAudio === lead.id ? '⏹️' : '▶️'}
                      </button>
                      {playingAudio === lead.id && audioDuration[lead.id] && (
                        <div style={{ width: '100%', marginTop: 4 }}>
                          <input
                            type="range"
                            min="0"
                            max={audioDuration[lead.id]}
                            value={audioProgress[lead.id] || 0}
                            onChange={(e) => handleSeek(lead.id, parseFloat(e.target.value))}
                            style={{
                              width: '100%',
                              height: '4px',
                              cursor: 'pointer',
                              accentColor: '#4caf50'
                            }}
                          />
                          <div style={{ fontSize: '10px', textAlign: 'center', marginTop: 2 }}>
                            {formatTime(audioProgress[lead.id] || 0)} / {formatTime(audioDuration[lead.id])}
                          </div>
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                        <span style={{ fontSize: 10, color: '#666' }}></span>
                        <select
                          value={playbackSpeed}
                          onChange={(e) => handleSpeedChange(parseFloat(e.target.value))}
                          style={{ fontSize: 12, padding: '2px 4px' }}
                          title="Скорость воспроизведения"
                        >
                          <option value={1}>1x</option>
                          <option value={1.25}>1.25x</option>
                          <option value={1.5}>1.5x</option>
                          <option value={2}>2x</option>
                        </select>
                      </div>
                      <button
                        onClick={() => handleDownloadAudio(lead.mp3_url!, lead.id)}
                        style={{ 
                          padding: '4px 8px', 
                          backgroundColor: 'transparent', 
                          color: '#2196f3', 
                          border: 'none', 
                          borderRadius: 4, 
                          cursor: 'pointer',
                          fontSize: 12
                        }}
                        title="Скачать аудио"
                      >
                        📥
                      </button>
                    </div>
                  ) : (
                    <span style={{ color: '#999', fontSize: 12 }}>Нет записи</span>
                  )}
                </td>
                <td style={{ padding: 8, border: '1px solid #ddd', height: '100px' }}>
                  <textarea
                    value={lead.note || ''}
                    onChange={(e) => {
                      const newNote = e.target.value;
                      // Обновляем локальное состояние немедленно через родительский компонент
                      if (onUpdate) {
                        onUpdate(lead.id, { note: newNote });
                      }
                    }}
                    onBlur={() => updateLeadField(lead.id, 'note', lead.note || '')}
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      border: '1px solid #ccc', 
                      resize: 'none', 
                      backgroundColor: 'white',
                      fontFamily: 'inherit',
                      fontSize: '12px',
                      lineHeight: '1.2',
                      padding: '4px',
                      outline: 'none'
                    }}
                    placeholder="Введите примечание..."
                  />
                </td>
                <td style={{ padding: 8, border: '1px solid #ddd', minHeight: '160px', minWidth: 260, verticalAlign: 'top' }}>
                  <textarea
                    value={lead.comment || ''}
                    onChange={(e) => {
                      const newComment = e.target.value;
                      // Обновляем локальное состояние немедленно через родительский компонент
                      if (onUpdate) {
                        onUpdate(lead.id, { comment: newComment });
                      }
                    }}
                    onBlur={() => updateLeadField(lead.id, 'comment', lead.comment || '')}
                    style={{
                      width: '100%',
                      minHeight: 160,
                      minWidth: 240,
                      border: '1px solid #ccc',
                      resize: 'both',
                      backgroundColor: 'white',
                      fontFamily: 'inherit',
                      fontSize: '13px',
                      lineHeight: '1.4',
                      padding: '6px',
                      outline: 'none'
                    }}
                    placeholder="Введите комментарий..."
                  />
                </td>
                <td style={{ padding: 8, border: '1px solid #ddd' }}>
                  <input
                    type="text"
                    value={lead.customer_name || ''}
                    onChange={(e) => {
                      const newCustomerName = e.target.value;
                      // Обновляем локальное состояние немедленно через родительский компонент
                      if (onUpdate) {
                        onUpdate(lead.id, { customer_name: newCustomerName });
                      }
                    }}
                    onBlur={() => updateLeadField(lead.id, 'name_note', lead.customer_name || '')}
                    style={{ 
                      width: '100%', 
                      border: '1px solid #ccc', 
                      backgroundColor: 'white',
                      fontSize: '12px',
                      padding: '4px',
                      outline: 'none'
                    }}
                    placeholder="Имя клиента..."
                  />
                </td>
                <td style={{ fontSize: '12px', padding: 8, border: '1px solid #ddd' }}>
                  {lead.client_manager_name ? (
                    <div style={{ maxWidth: 200, wordWrap: 'break-word', fontSize: '12px', lineHeight: '1.2' }}>
                      {lead.client_manager_name}
                    </div>
                  ) : (
                    <span style={{ color: '#999', fontSize: 10 }}>-</span>
                  )}
                </td>
                <td style={{ padding: 8, border: '1px solid #ddd', height: '100px' }}>
                  {lead.disput_comment ? (
                    <div style={{ maxWidth: 200, wordWrap: 'break-word', fontSize: '12px', lineHeight: '1.2' }}>
                      {lead.disput_comment}
                    </div>
                  ) : (
                    <span style={{ color: '#999', fontSize: 10 }}>-</span>
                  )}
                </td>
                <td style={{ padding: 8, border: '1px solid #ddd' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={lead.bonus || false}
                      onChange={(e) => {
                        const newBonusValue = e.target.checked;
                        // Обновляем локальное состояние немедленно через родительский компонент
                        if (onUpdate) {
                          onUpdate(lead.id, { bonus: newBonusValue });
                        }
                        // Вызываем функцию обновления на сервере
                        updateBonusStatus(lead.id, newBonusValue);
                      }}
                      style={{ 
                        width: '16px', 
                        height: '16px', 
                        cursor: 'pointer',
                        accentColor: '#4caf50'
                      }}
                    />
                    <span style={{ 
                      padding: '2px 6px', 
                      borderRadius: 4, 
                      backgroundColor: lead.bonus ? '#4caf50' : '#f44336',
                      color: 'white',
                      fontSize: 12
                    }}>
                      {lead.bonus ? 'Да' : 'Нет'}
                    </span>
                  </div>
                </td>
                <td style={{ padding: 8, border: '1px solid #ddd', textAlign: 'center' }}>
                  {assessmentsByLeadId[lead.id] ? (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleQualityClick(assessmentsByLeadId[lead.id], lead)}
                      sx={{ 
                        minWidth: 'auto', 
                        padding: '4px 8px',
                        fontSize: '11px',
                        borderColor: '#ccc',
                        color: '#333'
                      }}
                    >
                      <Chip
                        label={`${assessmentsByLeadId[lead.id].sale_probability}%`}
                        color={getScoreColor(assessmentsByLeadId[lead.id].sale_probability) as any}
                        size="small"
                        sx={{ fontSize: '10px', height: '20px' }}
                      />
                    </Button>
                  ) : (
                    <span style={{ color: '#999', fontSize: '11px' }}>—</span>
                  )}
                </td>
                <td style={{ padding: 8, border: '1px solid #ddd' }}>
                  <input
                    type="number"
                    value={lead.price || ''}
                    onChange={(e) => {
                      const newPrice = e.target.value ? parseFloat(e.target.value) : undefined;
                      // Обновляем локальное состояние немедленно через родительский компонент
                      if (onUpdate) {
                        onUpdate(lead.id, { price: newPrice });
                      }
                    }}
                    onBlur={() => updateLeadField(lead.id, 'price', lead.price || null)}
                    style={{ 
                      width: '100%', 
                      border: '1px solid #ccc', 
                      backgroundColor: 'white',
                      fontSize: '12px',
                      padding: '4px',
                      outline: 'none'
                    }}
                    placeholder="Цена"
                    min="0"
                    step="1"
                  />
                </td>
                <td style={{ padding: 8, border: '1px solid #ddd' }}>
                  <textarea
                    value={lead.address || ''}
                    onChange={(e) => {
                      const newAddress = e.target.value;
                      // Обновляем локальное состояние немедленно через родительский компонент
                      if (onUpdate) {
                        onUpdate(lead.id, { address: newAddress });
                      }
                    }}
                    onBlur={() => updateLeadField(lead.id, 'address', lead.address || '')}
                    style={{ 
                      width: '100%', 
                      minHeight: '50px',
                      border: '1px solid #ccc', 
                      resize: 'none', 
                      backgroundColor: 'white',
                      fontFamily: 'inherit',
                      fontSize: '12px',
                      padding: '4px',
                      outline: 'none'
                    }}
                    placeholder="Адрес"
                  />
                </td>
                <td style={{ padding: 8, border: '1px solid #ddd' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button
                      onClick={() => onPublishToMarketplace && onPublishToMarketplace(lead)}
                      disabled={!lead.phone}
                      style={{
                        padding: '6px',
                        backgroundColor: 'transparent',
                        color: !lead.phone ? '#ccc' : '#9c27b0',
                        border: 'none',
                        borderRadius: 4,
                        cursor: !lead.phone ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '32px',
                        height: '32px'
                      }}
                      title={!lead.phone ? 'Нет телефона' : 'Опубликовать на маркетплейс'}
                    >
                      <StorefrontIcon style={{ fontSize: 16 }} />
                    </button>
                    <button
                      onClick={() => handleEdit(lead)}
                      style={{
                        padding: '6px',
                        backgroundColor: 'transparent',
                        color: '#2196f3',
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '32px',
                        height: '32px'
                      }}
                      title="Редактировать"
                    >
                      <EditIcon style={{ fontSize: 16 }} />
                    </button>
                    <button
                      onClick={() => handleSendToClient(lead.id)}
                      disabled={lead.is_sent}
                      style={{ 
                        padding: '6px', 
                        backgroundColor: 'transparent', 
                        color: lead.is_sent ? '#ccc' : '#ff9800', 
                        border: 'none', 
                        borderRadius: 4, 
                        cursor: lead.is_sent ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '32px',
                        height: '32px'
                      }}
                      title={lead.is_sent ? 'Уже отправлен' : 'Отправить клиенту'}
                    >
                      <SendIcon style={{ fontSize: 16 }} />
                    </button>
                    <button
                      onClick={() => handleMarkAsInvalid(lead.id)}
                      style={{ 
                        padding: '6px', 
                        backgroundColor: 'transparent', 
                        color: '#f44336', 
                        border: 'none', 
                        borderRadius: 4, 
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '32px',
                        height: '32px'
                      }}
                      title="Пометить как брак"
                    >
                      <BlockIcon style={{ fontSize: 16 }} />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      
      {/* Модальное окно с детальной информацией об оценке качества */}
      <QualityAssessmentModal
        open={qualityModalOpen}
        onClose={() => setQualityModalOpen(false)}
        assessment={selectedAssessment}
        leadInfo={selectedAssessment ? leads.find(l => l.id === selectedAssessment.lead_id) : undefined}
      />
    </div>
  );
}; 